import { useState, useEffect, useCallback, useRef } from "react";
import { TenantService } from "@/services/tenantService";
import type { PlatformUser } from "@/types/tenant";
import toast from "react-hot-toast";
import {
  TbSearch,
  TbChevronDown,
  TbChevronRight,
  TbShieldPlus,
  TbShieldX,
  TbChevronLeft,
} from "react-icons/tb";

const ROLE_LABELS: Record<string, string> = {
  super_admin: "Super Admin",
  platform_admin: "Platform Admin",
};

const AVAILABLE_ROLES = ["super_admin", "platform_admin"];

export const PlatformUserManagement: React.FC = () => {
  const [users, setUsers] = useState<PlatformUser[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);
  const [userTenants, setUserTenants] = useState<
    {
      id: string;
      role: string;
      joined_at: string;
      tenant: { id: string; name: string; slug: string; is_active: boolean };
    }[]
  >([]);
  const [loadingTenants, setLoadingTenants] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadUsers = useCallback(
    async (searchTerm: string, pageNum: number) => {
      setLoading(true);
      try {
        const result = await TenantService.listPlatformUsers(
          searchTerm,
          pageNum,
          limit
        );
        setUsers(result.users);
        setTotal(result.total);
      } catch {
        toast.error("Failed to load users");
      } finally {
        setLoading(false);
      }
    },
    [limit]
  );

  useEffect(() => {
    loadUsers(search, page);
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      loadUsers(search, 1);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleExpandUser = async (userId: string) => {
    if (expandedUserId === userId) {
      setExpandedUserId(null);
      setUserTenants([]);
      return;
    }
    setExpandedUserId(userId);
    setLoadingTenants(true);
    try {
      const tenants = await TenantService.getUserTenants(userId);
      setUserTenants(tenants);
    } catch {
      toast.error("Failed to load user tenants");
    } finally {
      setLoadingTenants(false);
    }
  };

  const handleAssignRole = async (userId: string, roleName: string) => {
    try {
      await TenantService.assignPlatformRole(userId, roleName);
      toast.success(`Assigned ${ROLE_LABELS[roleName] || roleName}`);
      await loadUsers(search, page);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to assign role"
      );
    }
  };

  const handleRemoveRole = async (userId: string, roleName: string) => {
    if (!confirm(`Remove ${ROLE_LABELS[roleName] || roleName} role?`)) return;
    try {
      await TenantService.removePlatformRole(userId, roleName);
      toast.success(`Removed ${ROLE_LABELS[roleName] || roleName}`);
      await loadUsers(search, page);
    } catch {
      toast.error("Failed to remove role");
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="w-full space-y-4">
      <h2 className="text-lg font-semibold text-[#444]">User Management</h2>

      {/* Search */}
      <div className="relative">
        <TbSearch className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#828282]" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or email..."
          className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#374795] focus:border-transparent"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-4 border-[#374795] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : users.length === 0 ? (
        <div className="text-center py-12 text-[#828282] text-sm">
          No users found.
        </div>
      ) : (
        <>
          {/* User table */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-4 py-3 font-medium text-[#828282] w-10"></th>
                  <th className="text-left px-4 py-3 font-medium text-[#828282]">
                    Name
                  </th>
                  <th className="text-left px-4 py-3 font-medium text-[#828282]">
                    Email
                  </th>
                  <th className="text-left px-4 py-3 font-medium text-[#828282]">
                    Platform Roles
                  </th>
                  <th className="text-left px-4 py-3 font-medium text-[#828282]">
                    Tenants
                  </th>
                  <th className="text-left px-4 py-3 font-medium text-[#828282]">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => {
                  const isExpanded = expandedUserId === user.id;
                  const existingRoles = user.platform_roles.map(
                    (r) => r.role_name
                  );
                  const assignableRoles = AVAILABLE_ROLES.filter(
                    (r) => !existingRoles.includes(r)
                  );

                  return (
                    <UserRow
                      key={user.id}
                      user={user}
                      isExpanded={isExpanded}
                      existingRoles={existingRoles}
                      assignableRoles={assignableRoles}
                      userTenants={isExpanded ? userTenants : []}
                      loadingTenants={isExpanded && loadingTenants}
                      onExpand={() => handleExpandUser(user.id)}
                      onAssignRole={(role) => handleAssignRole(user.id, role)}
                      onRemoveRole={(role) => handleRemoveRole(user.id, role)}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-[#828282]">
                Showing {(page - 1) * limit + 1}–{Math.min(page * limit, total)}{" "}
                of {total}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-2.5 text-[#828282] hover:text-[#444] hover:bg-gray-100 rounded-lg disabled:opacity-30 transition-colors cursor-pointer"
                >
                  <TbChevronLeft className="w-5 h-5" />
                </button>
                <span className="text-sm text-[#444] px-2">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="p-2.5 text-[#828282] hover:text-[#444] hover:bg-gray-100 rounded-lg disabled:opacity-30 transition-colors cursor-pointer"
                >
                  <TbChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

interface UserRowProps {
  user: PlatformUser;
  isExpanded: boolean;
  existingRoles: string[];
  assignableRoles: string[];
  userTenants: {
    id: string;
    role: string;
    joined_at: string;
    tenant: { id: string; name: string; slug: string; is_active: boolean };
  }[];
  loadingTenants: boolean;
  onExpand: () => void;
  onAssignRole: (role: string) => void;
  onRemoveRole: (role: string) => void;
}

const UserRow: React.FC<UserRowProps> = ({
  user,
  isExpanded,
  existingRoles,
  assignableRoles,
  userTenants,
  loadingTenants,
  onExpand,
  onAssignRole,
  onRemoveRole,
}) => {
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);

  return (
    <>
      <tr className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
        <td className="px-4 py-3">
          <button
            onClick={onExpand}
            className="p-1.5 text-[#828282] hover:text-[#444] transition-colors cursor-pointer"
          >
            {isExpanded ? (
              <TbChevronDown className="w-4 h-4" />
            ) : (
              <TbChevronRight className="w-4 h-4" />
            )}
          </button>
        </td>
        <td className="px-4 py-3 text-[#444] font-medium">
          {user.first_name || user.last_name
            ? `${user.first_name || ""} ${user.last_name || ""}`.trim()
            : "—"}
        </td>
        <td className="px-4 py-3 text-[#828282]">{user.email}</td>
        <td className="px-4 py-3">
          <div className="flex flex-wrap gap-1.5">
            {existingRoles.length === 0 ? (
              <span className="text-[#828282] text-sm">None</span>
            ) : (
              existingRoles.map((role) => (
                <span
                  key={role}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-indigo-100 text-indigo-700 text-xs font-medium rounded-full"
                >
                  {ROLE_LABELS[role] || role}
                  <button
                    onClick={() => onRemoveRole(role)}
                    className="hover:text-red-600 transition-colors cursor-pointer"
                    title={`Remove ${ROLE_LABELS[role] || role}`}
                  >
                    <TbShieldX className="w-3.5 h-3.5" />
                  </button>
                </span>
              ))
            )}
          </div>
        </td>
        <td className="px-4 py-3 text-[#444]">{user.tenant_count}</td>
        <td className="px-4 py-3">
          {assignableRoles.length > 0 && (
            <div className="relative">
              <button
                onClick={() => setShowRoleDropdown(!showRoleDropdown)}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-[#374795] hover:bg-indigo-50 rounded-lg transition-colors cursor-pointer"
              >
                <TbShieldPlus className="w-4 h-4" /> Add Role
              </button>
              {showRoleDropdown && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowRoleDropdown(false)}
                  />
                  <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1 min-w-[180px]">
                    {assignableRoles.map((role) => (
                      <button
                        key={role}
                        onClick={() => {
                          onAssignRole(role);
                          setShowRoleDropdown(false);
                        }}
                        className="w-full text-left px-4 py-2.5 text-sm text-[#444] hover:bg-gray-50 transition-colors cursor-pointer"
                      >
                        {ROLE_LABELS[role] || role}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </td>
      </tr>

      {/* Expanded row */}
      {isExpanded && (
        <tr>
          <td colSpan={6} className="px-4 py-4 bg-gray-50">
            {loadingTenants ? (
              <div className="flex justify-center py-3">
                <div className="w-5 h-5 border-2 border-[#374795] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : userTenants.length === 0 ? (
              <p className="text-sm text-[#828282]">No tenant memberships</p>
            ) : (
              <div className="space-y-2">
                <p className="text-sm font-medium text-[#444] mb-2">
                  Tenant Memberships
                </p>
                {userTenants.map((m) => (
                  <div
                    key={m.id}
                    className="flex items-center justify-between px-4 py-2.5 bg-white rounded-lg border border-gray-200 shadow-sm"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium text-[#444]">
                        {m.tenant.name}
                      </span>
                      <span className="text-sm text-[#828282]">
                        {m.tenant.slug}
                      </span>
                      {!m.tenant.is_active && (
                        <span className="text-xs font-medium text-red-500">
                          (Inactive)
                        </span>
                      )}
                    </div>
                    <span className="text-xs font-medium px-2.5 py-1 bg-gray-100 text-[#444] rounded-full capitalize">
                      {m.role}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
};
